import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useI18n } from '../lib/i18n';
import { formatRSD } from '../lib/helpers';
import Icon from './Icon';

const fmtD = (d) => { const v = String(d || '').slice(0, 10); return v ? v.split('-').reverse().join('.') + '.' : '—'; };

// Poruka greške iz Supabase funkcije
export async function fnError(error, data) {
  if (data?.error) return data.error;
  if (!error) return '';
  try { const b = await error.context?.json(); if (b?.error) return b.error + (b.detail ? ` (${b.detail})` : ''); } catch { /* */ }
  return error.message;
}

// Statusi iz SEF-a, na srpskom
export const SEF_STATUS = {
  New: 'Nova', Draft: 'Nacrt', Sending: 'Šalje se', Sent: 'Poslata', Seen: 'Viđena', Approved: 'Prihvaćena',
  ApprovedWithReservation: 'Prihvaćena s rezervom', Rejected: 'Odbijena', Cancelled: 'Otkazana', Storno: 'Stornirana',
  Mistake: 'Greška', Overdue: 'Istekao rok', Reminded: 'Podsetnik', Deleted: 'Obrisana',
};
export const sefLabel = (s) => SEF_STATUS[s] || SEF_STATUS[String(s || '').charAt(0).toUpperCase() + String(s || '').slice(1).toLowerCase()] || s || '—';

// ============================================================
// "Pošalji u SEF (eFaktura)" — potvrda i slanje jedne fakture
// ============================================================
export default function SefSendDialog({ invoice, customerName, onClose, onSent }) {
  const { tr } = useI18n();
  const [env, setEnv] = useState(null);
  const [cir, setCir] = useState(false);
  const [jbkjs, setJbkjs] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  // SEF prihvata samo današnji datum fakture
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const oldDate = String(invoice.issue_date || '').slice(0, 10) !== today;
  const [fixDate, setFixDate] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('sef_settings').select('environment').eq('agency_id', invoice.agency_id).maybeSingle();
      setEnv(data ? data.environment : 'none');
    })();
  }, [invoice.agency_id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const send = async () => {
    setBusy(true); setErr('');
    const { data, error } = await supabase.functions.invoke('sef', { body: { action: 'send', invoice_id: invoice.id, send_to_cir: cir, jbkjs, update_issue_date: oldDate && fixDate } });
    setBusy(false);
    const e = await fnError(error, data);
    if (e) { setErr(e); return; }
    setDone(data);
    const { data: fresh } = await supabase.from('invoices').select('*').eq('id', invoice.id).maybeSingle();
    if (fresh) onSent?.(fresh);
  };

  return (
    <div style={s.overlay} className="no-print">
      <div style={s.modal} role="dialog" aria-modal="true">
        <div style={s.head}>
          <h2 style={s.title}>{tr('Pošalji u SEF (eFaktura)', 'Send to SEF (e-invoice)', 'Отправить в SEF')}</h2>
          <button onClick={onClose} disabled={busy} style={s.close} aria-label={tr('Zatvori', 'Close', 'Закрыть')}><Icon name="x" size={18} /></button>
        </div>
        <div style={s.sub}>
          <strong>{invoice.invoice_number}</strong> · {customerName || '—'} · {formatRSD(invoice.amount)}
        </div>

        {env === 'none' && (
          <div style={s.warn}>{tr('Prvo unesite SEF API ključ: eFakture → Povezivanje sa SEF-om.', 'Add your SEF API key first: e-Invoices → Connect to SEF.', 'Сначала введите API-ключ SEF.')}</div>
        )}
        {env && env !== 'none' && (
          <div style={{ ...s.envTag, ...(env === 'prod' ? s.envProd : {}) }} className="mono">
            {env === 'prod' ? tr('PRODUKCIJA — prava eFaktura', 'PRODUCTION — real e-invoice', 'ПРОДАКШН') : tr('DEMO — probno slanje, bez pravnog dejstva', 'DEMO — test, no legal effect', 'ДЕМО — тест')}
          </div>
        )}

        {done ? (
          <div style={s.ok}>
            {tr('Faktura je poslata u SEF. Broj u SEF-u:', 'Invoice sent to SEF. SEF id:', 'Счёт отправлен. ID:')} <strong className="mono">{done.sef_invoice_id}</strong>
            <div style={{ marginTop: 10 }}><button onClick={onClose} style={s.primary}>{tr('Zatvori', 'Close', 'Закрыть')}</button></div>
          </div>
        ) : (
          <>
            <p style={s.text}>
              {tr('Faktura se šalje kupcu preko državnog Sistema elektronskih faktura, kao paušalac koji nije u sistemu PDV-a (kategorija SS, član 33). Posle slanja više ne može da se menja — može samo da se otkaže dok je kupac ne prihvati.',
                'The invoice is sent through the national e-invoice system as a non-VAT flat-rate entrepreneur. After sending it cannot be edited — only cancelled before the buyer accepts it.',
                'Счёт отправляется через систему SEF. После отправки изменить нельзя — только отменить до принятия.')}
            </p>
            {oldDate && (
              <div style={s.warn}>
                {tr(`SEF prihvata samo današnji datum fakture. Faktura će u SEF otići sa datumom ${fmtD(today)} (datum prometa ostaje ${fmtD(invoice.service_date || invoice.issue_date)}).`,
                  `SEF only accepts today's invoice date. It will be sent dated ${fmtD(today)} (date of supply stays ${fmtD(invoice.service_date || invoice.issue_date)}).`,
                  `SEF принимает только сегодняшнюю дату (${fmtD(today)}).`)}
                <label style={{ ...s.check, marginTop: 8 }}>
                  <input type="checkbox" checked={fixDate} onChange={(e) => setFixDate(e.target.checked)} />
                  <span>{tr('Promeni i datum fakture u aplikaciji na današnji (preporučeno, da se poklapa sa SEF-om i KPO knjigom)', 'Also change the invoice date in the app to today (recommended)', 'Изменить дату счёта на сегодня')}</span>
                </label>
              </div>
            )}
            <label style={s.check}>
              <input type="checkbox" checked={cir} onChange={(e) => setCir(e.target.checked)} />
              <span>{tr('Kupac je iz javnog sektora (upis u Centralni registar faktura)', 'Buyer is a public-sector entity (CRF)', 'Покупатель — госсектор (CRF)')}</span>
            </label>
            {cir && (
              <input value={jbkjs} onChange={(e) => setJbkjs(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder={tr('JBKJS kupca (5 cifara)', 'Buyer JBKJS (5 digits)', 'JBKJS (5 цифр)')} style={s.input} inputMode="numeric" />
            )}
            {err && <div style={s.err}>{err}</div>}
            <div style={s.btns}>
              <button onClick={onClose} disabled={busy} style={s.cancel}>{tr('Otkaži', 'Cancel', 'Отмена')}</button>
              <button onClick={send} disabled={busy || env === 'none' || env === null} style={{ ...s.primary, ...(busy || env === 'none' ? { opacity: 0.6 } : {}) }}>
                <Icon name="logout" size={14} />{busy ? tr('Šaljem…', 'Sending…', 'Отправка…') : tr('Pošalji u SEF', 'Send to SEF', 'Отправить')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(12,37,48,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, textAlign: 'left' },
  modal: { background: 'var(--surface)', borderRadius: 14, padding: 22, width: '100%', maxWidth: 480, boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', gap: 12 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  title: { margin: 0, fontSize: 17, fontWeight: 700 },
  close: { width: 32, height: 32, border: 'none', background: 'transparent', borderRadius: 8, color: 'var(--ink-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  sub: { fontSize: 13, color: 'var(--ink-700)' },
  envTag: { alignSelf: 'flex-start', fontSize: 11, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 6, background: 'var(--lavender-100)', color: 'var(--lavender-700)' },
  envProd: { background: 'var(--teal-800)', color: '#fff' },
  text: { margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--ink-700)' },
  check: { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--ink-700)' },
  input: { height: 36, border: '1px solid var(--line)', borderRadius: 8, padding: '0 10px', fontSize: 14 },
  warn: { fontSize: 13, background: 'var(--lavender-100)', color: 'var(--ink-900)', borderRadius: 8, padding: '8px 10px' },
  err: { fontSize: 12.5, background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 8, padding: '8px 10px', wordBreak: 'break-word' },
  ok: { fontSize: 13, background: 'var(--lavender-100)', color: 'var(--ink-900)', borderRadius: 8, padding: '10px 12px' },
  btns: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 },
  cancel: { minHeight: 34, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 8, padding: '0 14px', fontWeight: 600, fontSize: 13, color: 'var(--ink-700)', cursor: 'pointer' },
  primary: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
};
